import { Box, Button, Card, Grid, MenuItem, Select, Tooltip, Typography, CircularProgress } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { postSetTicketMessagesRead, postPinTicket, getTicketSummary } from '../../../../api/tickets';
import { getUnreadData } from '../../../../redux/slices/dashboard';
import { editTicketData, setBoomerangDate, updateTicketInfo } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { priorityColor } from '../../../../utils/cssStyles';
import { markMetricAdded, NotificationMetricType } from '../../../../redux/slices/notifications';
import { fDateTime } from '../../../../utils/formatTime';
import { inThreeDays, tomorrow, inXHours } from './RemindMeTimers';
import TicketCategory from './TicketCategory';
import Iconify from '../../../../reusable-components/iconify';
import Scrollbar from "../../../../reusable-components/scrollbar";
import TicketAutoCompleteActions from './TicketAutoCompleteActions';


export default function TicketActions() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id = '' } = useParams();
  const hasEditAccess = useScopeCheck(['Tickets-Edit'], true);
  const subticketId = new URLSearchParams(location.search).get('subticket');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { ticketNumber, ticketMessages = [] } = useSelector((state) => state.tickets.data.ticketById[subticketId || id]) || {};
  const {
    data: { ticketTypes = [] },
  } = useSelector((state) => state.tickets) || {};
  const { mainTicket, boomerang, ticketCategories, subtickets } =
    useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};

  const currentTicketData = subticketId
    ? subtickets?.find(st => st.id === parseInt(subticketId, 10))
    : mainTicket;
  const [priority, setPriority] = useState(mainTicket?.importance || 'Medium');
  const [showCustomBoomerangDate, setShowCustomBoomerangDate] = useState(false);
  const [removeBoomerangTooltip, setRemoveBoomerangTooltip] = useState(false);
  const [selectedTicketCategory, setSelectedTicketCategory] = useState(null);
  const isPinned = mainTicket?.aboveLine === true;
  const [isTicketPriorityLoading, setIsTicketPriorityLoading] = useState(false);
  useEffect(() => {
    setSelectedTicketCategory(
      ticketCategories?.find((val) => val.value === mainTicket?.ticketCategoryId)?.value || null
    );
  }, [mainTicket, ticketCategories]);

  const editTicket = async ({ Id, value, updateType }) => {
    const response = await dispatch(editTicketData({ Id, value, updateType }));
    if (response) enqueueSnackbar('Ticket updated successfully', { variant: 'success' });
    else enqueueSnackbar('Ticket failed to update', { variant: 'error' });
    return response;
  };
  const handleUnread = async () => {
    const response = await postSetTicketMessagesRead({ id, markAsUnread: true });
    if (response.status === 200) {
      dispatch(getUnreadData());
      dispatch(markMetricAdded(NotificationMetricType.TicketsUnread, id));
    }
    navigate(`/dashboard/tickets`);
  };

  const handleChange = (event) => {
    setIsTicketPriorityLoading(true);
    editTicket({ Id: id === "" ? ticketNumber : id, value: event.target.value, updateType: 'Importance' }).then((res) => {
      if (res) setPriority(event.target.value);
      setIsTicketPriorityLoading(false);
    });
  };

  const updateBoomerangDate = async (date) => {
    await dispatch(setBoomerangDate(date, id, enqueueSnackbar));
  };

  const handlePin = async (ticketId, action) => {
    try {
      const { data } = await postPinTicket({ ticketId });
      if (data) {
        enqueueSnackbar(`Ticket ${ticketId} ${action === 'pinned' ? 'pinned' : 'unpinned'} successfully`, {
          variant: 'success',
        });
        dispatch(updateTicketInfo({ ...mainTicket, aboveLine: data.aboveLine }));
      } else {
        enqueueSnackbar(`Ticket ${ticketId} ${action === 'pinned' ? 'pinned' : 'unpinned'} failed`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(`Error: Could not ${action} ticket ${ticketId}`, { variant: 'error' });
    }
  };

  useEffect(() => {
    setPriority(mainTicket?.importance || 'Medium');
  }, [mainTicket]);

  useEffect(() => {
    setShowCustomBoomerangDate(!!boomerang);
  }, [boomerang]);

  const [ticketSummary, setTicketSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  const getTicketSummaryData = async () => {
      setIsLoadingSummary(true);
      try {
        const result = await getTicketSummary({ ticketId: parseInt(subticketId || id, 10) });
        setTicketSummary(result.data);
      } catch (err) {
        setTicketSummary(null);
      } finally {
        setIsLoadingSummary(false);
      }
    }

  useEffect(() => {
    if (currentTicketData?.summary) {
      const summaryArray = currentTicketData.summary.split('\n').filter(line => line.trim());
      setTicketSummary({ summary: summaryArray });
    } else {
      setTicketSummary(null);
    }
  }, [currentTicketData?.summary, subticketId]);
  return (
    <>
      <Card sx={{ height: '100%', mb: 2, p: 2 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h7" color="info.main" sx={{ fontWeight: 'bold', mb: 2 }}>
            Ticket Details:
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '14px', mr: 1 }}>Ticket Category:</Typography>

          <Box sx={{ overflow: 'hidden' }}>
            <TicketCategory
              ticketId={id}
              selectedTicketCategory={selectedTicketCategory}
              setSelectedTicketCategory={setSelectedTicketCategory}
            />
          </Box>
        </Box>

        <Grid container alignItems="center">
          <Grid item>
            <Typography sx={{ fontSize: '14px' }}>Priority Level:</Typography>
          </Grid>
          <Grid item>
            <Box>
              <Select
                value={priority}
                onChange={handleChange}
                displayEmpty
                disabled={isTicketPriorityLoading}
                renderValue={(selected) =>
                  isTicketPriorityLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : selected
                }
                MenuProps={{
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '0.85rem',
                    },
                  },
                }}
                sx={{
                  height: 32,
                  maxHeight: '30px',
                  color: (theme) => priorityColor(priority, theme),
                  '& .MuiSelect-select': {
                    padding: '4px 10px',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiSelect-icon': {
                    color: (theme) => priorityColor(priority, theme),
                  },
                }}
              >
                <MenuItem value={'Low'}>Low</MenuItem>
                <MenuItem value={'Medium'}>Medium</MenuItem>
                <MenuItem value={'High'}>High</MenuItem>
                <MenuItem value={'Maximum'}>Maximum</MenuItem>
              </Select>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '14px' }}>Ticket Type:</Typography>

          <Typography sx={{ fontSize: '14px' }}>
            {ticketTypes?.find((type) => type.id === mainTicket?.ticketTypeId)?.name || 'Other'}
          </Typography>
        </Box>
      </Card>{' '}
      <Card sx={{ height: '100%', mb: 2, p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h7" color="info.main" sx={{ mb: 2, fontWeight: 'bold' }}>
            Remind me:
          </Typography>
        </Box>
        <Grid justifyContent="center" alignItems="center" container spacing={1.5}>
          <Grid item xs={6}>
            <AwaitButton
              sx={{ width: '100%' }}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={async () => {
                await updateBoomerangDate(inXHours(1));
              }}
            >
              In one hour
            </AwaitButton>
          </Grid>
          <Grid item xs={6}>
            <AwaitButton
              sx={{ width: '100%' }}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={async () => {
                await updateBoomerangDate(inThreeDays());
              }}
            >
              In three days
            </AwaitButton>
          </Grid>
          <Grid item xs={6}>
            <AwaitButton
              sx={{ width: '100%' }}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={async () => {
                await updateBoomerangDate(tomorrow());
              }}
            >
              Tomorrow
            </AwaitButton>
          </Grid>
          <Grid item xs={6}>
            <Button
              sx={{ width: '100%' }}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={() => {
                setShowCustomBoomerangDate(true);
              }}
            >
              Custom time
            </Button>
          </Grid>
          <Tooltip
            arrow
            title={
              boomerang?.boomerangOn && !removeBoomerangTooltip
                ? `Ticket will be moved above the ticket line at ${fDateTime(boomerang.boomerangOn)}`
                : ''
            }
          >
            {showCustomBoomerangDate ? (
              <Grid item xs={12}>
                <DateTimePicker
                  sx={{ width: "100%" }}
                  minDate={new Date()}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['clear', 'cancel', 'accept'] },
                  }}
                  value={boomerang?.boomerangOn ? new Date(boomerang.boomerangOn) : null}
                  onAccept={(dateValue) => {
                    updateBoomerangDate(dateValue);
                    setRemoveBoomerangTooltip(false);
                  }}
                  onClose={() => setRemoveBoomerangTooltip(false)}
                  onOpen={() => setRemoveBoomerangTooltip(true)}
                />
              </Grid>
            ) : (
              <></>
            )}
          </Tooltip>
        </Grid>
      </Card>
      <Card sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TicketAutoCompleteActions id={id} ticketNumber={ticketNumber || id} />
          </Grid>
          {hasEditAccess && (
            <Grid item xs={12} xl={6}>
              <AwaitButton
                fullWidth
                variant="contained"
                size="small"
                color="secondary"
                onClick={handleUnread}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Mark as unread
                <Iconify icon="material-symbols:mail-outline" width={18} sx={{ ml: 0.5 }} />
              </AwaitButton>
            </Grid>
          )}
          <Grid item xs={12} xl={hasEditAccess ? 6 : 12}>
            <AwaitButton
              fullWidth
              variant={isPinned ? 'contained' : 'outlined'}
              size="small"
              color="secondary"
              onClick={() => handlePin(id, isPinned ? 'unpinned' : 'pinned')}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {isPinned ? 'Unpin Ticket' : 'Pin Ticket'}
              <Iconify icon="mdi:pin-outline" width={18} sx={{ ml: 0.5 }} />
            </AwaitButton>
          </Grid>
        </Grid>
      </Card>
      <Card sx={{ height: '100%', mb: 2, p: 2 }}>
        <Scrollbar style={{ maxHeight: 300, paddingRight: 8 }}>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/assets/bobAI/bobAIlogo_2.png" alt="bobAI Icon" style={{ width: 20, height: 20 }} />
            <Typography variant="h7" color="info.main" sx={{ fontWeight: 'bold' }}>
              {subticketId ? 'Subticket Summary:' : 'Ticket Summary:'}
            </Typography>
          </Box>
          {isLoadingSummary ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : ticketSummary ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {ticketSummary?.summary?.length > 0 && (
                <Box>
                  {ticketSummary.summary.length === 1 ? (
                    <Typography sx={{ fontSize: '14px', mb: 0.5 }}>
                      {ticketSummary.summary[0]}
                    </Typography>
                  ) : (
                    <Box component="ul" sx={{ ml: 2, mt: 0.5, mb: 0 }}>
                      {ticketSummary.summary.map((item, index) => (
                        <Typography component="li" key={index} sx={{ fontSize: '14px', mb: 0.5 }}>
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: '8px', color: 'text.secondary' }}>
                    *Summary generated by BobAI based on ticket messages and may not capture all details.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              {ticketMessages.length === 0 ? (
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  No messages yet
                </Typography>
              ) : (
                <AwaitButton
                  fullWidth
                  variant="outlined"
                  size="small"
                  color="info"
                  onClick={getTicketSummaryData}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Generate Summary
                </AwaitButton>
              )}
            </Box>
          )}
        </Scrollbar>
      </Card>
    </>
  );
}
